import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { observer, inject } from 'mobx-react'
import { reaction, observable, comparer } from 'mobx'
import get from 'lodash/get'

import { NULL as BlockModelNULL } from '@client/components/Block/model'
import ProductModel, { NULL as ProductModelNULL } from './model'
import EditorModel, {
  NULL as EditorModelNULL,
} from '@client/components/Editor/model'
import AppModel, { NULL as AppModelNULL } from '@client/components/App/model'
import { options } from '@client/components/blocks/Text/options'
import { updateDataInline } from '@client/components/blocks/Text/plugins/updateDataInline'
import findAncestor from '@client/utils/findAncestor'
import { isHttpImageUrl } from '@client/utils/helpers'
import reservedImageHeight from '@client/utils/reservedImageHeight'

import './styles.scss'

const IMAGE_PLACEHOLDER_PATH =
  'https://cdn.getanewsletter.com/blockeditor/placeholder-image.png'

export class Product extends Component {
  @observable editingSettings = BlockModelNULL

  state = { imgError: false }

  static propTypes = {
    app: PropTypes.instanceOf(AppModel),
    model: PropTypes.instanceOf(ProductModel),
    editor: PropTypes.instanceOf(EditorModel),
    serverRender: PropTypes.bool,
  }

  static defaultProps = {
    app: AppModelNULL,
    model: ProductModelNULL,
    editor: EditorModelNULL,
    serverRender: false,
  }

  disposeEditingReaction = null
  disposeEditorReaction = null

  constructor(props) {
    super(props)

    this.el = React.createRef()

    this.editingSettings = this.props.app?.editingSettings || BlockModelNULL

    this.disposeEditingReaction = reaction(
      () => this.editingSettings,
      (values) => {
        this.setContainerDimentions()
      }
    )

    // Store references to observable objects (not props) for use in reaction
    // This avoids the "Cannot read props in a reactive context" error
    const editor = props.editor
    const model = props.model

    // Track specific settings properties that updateDataInline uses
    // This ensures the reaction fires when any of these values change
    this.disposeEditorReaction = reaction(
      () => {
        const values = editor.settings.values
        return {
          // Heading settings
          headings_h1FontSize: values.headings_h1FontSize,
          headings_h2FontSize: values.headings_h2FontSize,
          headings_h3FontSize: values.headings_h3FontSize,
          headings_h4FontSize: values.headings_h4FontSize,
          headings_color: values.headings_color,
          headings_fontWeight: values.headings_fontWeight,
          headings_fontFamily: values.headings_fontFamily,
          headings_lineHeight: values.headings_lineHeight,
          // Normal text settings
          normalText_color: values.normalText_color,
          normalText_fontFamily: values.normalText_fontFamily,
          normalText_fontSize: values.normalText_fontSize,
          normalText_lineHeight: values.normalText_lineHeight,
          // Link settings
          link_color: values.link_color,
        }
      },
      () => {
        const values = editor.settings.values
        model.setHtmlText(
          updateDataInline(values, model.htmlText)
        )
      },
      { delay: 100, equals: comparer.shallow }
    )

    // Reset imgError when the user changes imageUrl. Must be a mobx reaction
    // (not componentDidUpdate prevProps) — under @observer, prevProps.model
    // and this.props.model reference the same instance, so reading
    // prevProps.model.settings.values.imageUrl resolves to the new value too
    // and the comparison would always be equal.
    this.disposeImageUrlReaction = reaction(
      () => model.settings.values.imageUrl,
      () => {
        if (this.state.imgError) this.setState({ imgError: false })
      }
    )
  }

  componentDidMount() {
    this.tableCell = this.getTableCell()
    this.setContainerDimentions()
    if (this.tableCell) {
      this.resizeObserver = new ResizeObserver(() => this.setContainerDimentions())
      this.resizeObserver.observe(this.tableCell)
    }
  }

  componentDidUpdate(prevProps, prevState) {
    this.editingSettings = this.props.app?.editingSettings || BlockModelNULL
  }

  handleImgError = () => {
    if (!this.state.imgError) this.setState({ imgError: true })
  }

  componentWillUnmount() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    }
    if (this.disposeEditingReaction) {
      this.disposeEditingReaction()
    }
    if (this.disposeImageUrlReaction) {
      this.disposeImageUrlReaction()
    }
    if (this.disposeEditorReaction) {
      this.disposeEditorReaction()
    }
  }

  getTableCell = () => {
    return findAncestor(this.el.current, 'gan-block-parent')
  }

  setContainerDimentions() {
    const tableCell = this.tableCell
    const clientWidth = get(tableCell, 'clientWidth')
    this.props.model.setContainerDimensions({
      width: clientWidth,
    })
  }

  getStyles = () => {
    const styles = {
      borderCollapse: 'collapse',
      tableLayout: 'fixed',
      overflowWrap: 'break-word',
      wordWrap: 'break-word',
      wordBreak: 'break-word',
    }
    styles.paddingTop = '10px'
    return styles
  }

  render() {
    const { model, serverRender, app, editor } = this.props
    const { htmlText } = model
    const settings = model.settings.values
    const hasContent = htmlText && htmlText.trim() !== ''
    const content = hasContent
      ? htmlText
      : app.formatMessage(options.placeholder)

    const globalSettings = editor.settings.values
    const width = parseFloat(settings.width) || undefined
    const imageLink = settings.imageLink ? settings.imageLink.trim() : ''
    const trimmedImageUrl = settings.imageUrl ? settings.imageUrl.trim() : ''
    const useUserUrl =
      isHttpImageUrl(trimmedImageUrl) && !this.state.imgError
    const imageUrl = useUserUrl ? trimmedImageUrl : IMAGE_PLACEHOLDER_PATH
    // Only wire onError on the user-supplied URL; the placeholder doesn't
    // need its own load-fail handler.
    const imgOnError =
      serverRender || !useUserUrl ? undefined : this.handleImgError
    // Reserve the rendered box from the intrinsic dimensions captured at
    // image-set time so a blocked/images-off image doesn't collapse to 0px
    // (FE-1128). Skipped for the placeholder and when dimensions aren't known yet.
    const reservedHeight = useUserUrl
      ? reservedImageHeight(width, settings.naturalWidth, settings.naturalHeight)
      : undefined
    const altText = settings.altText ? settings.altText.trim() : ''

    const fontSize = settings.fontSize || globalSettings.normalText_fontSize

    const className = `${serverRender ? '' : 'image'} mobile-full-width`

    const imgStyle = {
      minHeight: serverRender ? undefined : '20px',
      display: 'block',
      border: 0,
      height: 'auto',
      margin: 0,
      width: width ? `${width}px` : undefined,
      maxWidth: width ? '100%' : undefined,
      fontSize: fontSize + 'px',
    }

    return (
      <table
        cellSpacing="0"
        cellPadding="0"
        border="0"
        role="presentation"
        width="100%"
        className={serverRender ? '' : 'product-block'}
      >
        <tbody>
          <tr>
            <td>
              <Choose>
                <When condition={imageLink}>
                  <a
                    className="gan-product-link"
                    href={imageLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ display: 'block' }}
                    ref={this.el}
                  >
                    <img
                      className={className}
                      src={imageUrl}
                      alt={altText}
                      width={width}
                      height={reservedHeight}
                      style={imgStyle}
                      onError={imgOnError}
                    />
                  </a>
                </When>
                <When condition={!imageLink}>
                  <img
                    className={className}
                    src={imageUrl}
                    alt={altText}
                    width={width}
                    height={reservedHeight}
                    style={imgStyle}
                    ref={this.el}
                    onError={imgOnError}
                  />
                </When>
              </Choose>
            </td>
          </tr>
          <tr>
            <td style={this.getStyles()}>
              <div dangerouslySetInnerHTML={{ __html: content }} />
            </td>
          </tr>
        </tbody>
      </table>
    )
  }
}

export default inject('app', 'editor', 'serverRender')(observer(Product))
